import React, { useState, useEffect } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Link } from "react-router-dom";
import DataTable, { createTheme } from "react-data-table-component";
import { useAtom } from "jotai";
import classes from "../../../assets/styles/Contact.module.css";
import { Card_URL } from "../../../utils/constants/apiUrl";
import { get, deleteRecord } from "../../../server";
import showToast from "../../../helpers/showToast";
import { loggedInUser } from "../../../utils/store";
import Loader from "../../../components/Loader";
import ConfirmationModal from "../../../components/Modals/ConfirmationModal";
import searchIcon from "../../../assets/images/global_icons/searchicon.svg";

const AllCards = () => {
  const [loggedUser] = useAtom(loggedInUser);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [cards, setCards] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Pagination states
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilter = (e) => {
    setFilterText(e.target.value);
  };

  const loadCards = async (page = 1, size = 10, search = "") => {
    setLoading(true);
    setIsDataLoaded(false);

    const params = new URLSearchParams({
      paginate: "true",
      page: page.toString(),
      per_page: size.toString(),
    });

    if (search && search.trim() !== "") {
      params.append("search", search.trim());
    }

    let { status, message, data } = await get(
      `${Card_URL}?${params.toString()}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );

    if (status) {
      setCards(data.data || []);
      setTotalRows(data.meta?.total || 0);
    } else {
      showToast(status, message);
    }
    setLoading(false);
    setIsDataLoaded(true);
  };

  useEffect(() => {
    loadCards(currentPage, perPage, filterText);
  }, [currentPage, perPage]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadCards(1, perPage, filterText);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filterText]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  createTheme(
    "solarized",
    {
      background: {
        default: "background: var(--secondary_color)",
      },
    },
    "dark"
  );

  const columns = [
    {
      name: "Card Type",
      width: "200px",
      cell: (row) => (
        <div className="profileWrapper">
          <p className="globalDetail">{row.card_type.toUpperCase()}</p>
        </div>
      ),
    },
    {
      name: "Name",
      width: "200px",
      cell: (row) => (
        <div className={"globalDetail"}>
          {row.first_name} {row.last_name}
        </div>
      ),
    },
    {
      name: "Username",
      width: "200px",
      cell: (row) => (
        <div className={"globalDetail"}>
          {row?.user_data?.first_name} {row?.user_data?.last_name}
        </div>
      ),
    },
    {
      name: "Created in",
      width: "150px",
      cell: (row) => (
        <div className={"globalDetail"}>
          {row.team_detail !== null ? "Team Mode" : "Individual"}
        </div>
      ),
    },
    {
      name: "Team Name",
      width: "200px",
      cell: (row) => (
        <div className={"globalDetail"}>
          {row.team_detail !== null ? row?.team_detail.name : "-"}
        </div>
      ),
    },
    {
      name: "Action",
      right: "true",
      cell: (row) => (
        <div className={classes.action}>
          <button
            className="btn customActionDropdown dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <BsThreeDotsVertical className={"globalVerticalThreeDots"} />
          </button>
          <ul className="customActionDropdownMenu dropdown-menu dropdown-menu-end ">
            <li>
              <Link className="dropdown-item" to={`ViewCard/${row.id}`}>
                View
              </Link>
            </li>
            <li>
              <Link className="dropdown-item" to={`AddEditCard/${row.id}`}>
                Edit
              </Link>
            </li>
            <li>
              <Link
                className="dropdown-item"
                to="#"
                style={{ color: "var(--error_color)" }}
                onClick={() => handleDelete(row.id)}
              >
                Delete
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  // Handle confirmation modal
  const handleShowConfirmationModal = () => setShowConfirmationModal(true);
  const handleCloseConfirmationModal = () => setShowConfirmationModal(false);

  // handle delete
  const handleDelete = (id) => {
    setDeleteId(id);
    handleShowConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    handleCloseConfirmationModal(true);
    setLoading(true);
    let { status, message } = await deleteRecord(
      `${Card_URL}/${deleteId}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );
    showToast(status, message);

    if (status) {
      await loadCards(currentPage, perPage, filterText);
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(totalRows / perPage);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="">
          <div className="page_Header">
            <div className="d-flex justify-content-between align-items-md-center align-items-initial flex-md-row flex-column gap-3 mb-3">
              <div className="heading">
                <h1 className="pageTitle">
                  Cards <span>({totalRows})</span>
                </h1>
              </div>
              <div
                className="input-group"
                style={{ maxWidth: "250px", height: "45px" }}
              >
                <span className="input-group-text bg-white border-end-0">
                  <img
                    src={searchIcon}
                    alt="search"
                    style={{ height: "16px" }}
                  />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search..."
                  value={filterText}
                  onChange={handleFilter}
                />
              </div>
            </div>
          </div>

          <div className="default-list-table">
            <DataTable columns={columns} data={cards} theme="solarized" />
            <div className="d-flex justify-content-center align-items-center p-3 flex-wrap gap-4 border-top">
              <div className="d-flex align-items-center gap-2">
                <span className="globalDetail">Rows per page:</span>
                <select
                  className="form-select"
                  style={{ width: "auto", padding: "6px 35px 6px 12px" }}
                  value={perPage}
                  onChange={(e) => handlePerRowsChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination mb-0">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      style={{
                        padding: "8px 16px",
                        minWidth: "45px",
                        textAlign: "center",
                        color: "var(--blue-color)",
                      }}
                    >
                      &laquo; Previous
                    </button>
                  </li>

                  {(() => {
                    const pages = [];

                    if (totalPages <= 8) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (currentPage <= 4) {
                        for (let i = 1; i <= 6; i++) pages.push(i);
                        pages.push("ellipsis-end");
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        pages.push(1);
                        pages.push("ellipsis-start");
                        for (let i = totalPages - 5; i <= totalPages; i++)
                          pages.push(i);
                      } else {
                        pages.push(1);
                        pages.push("ellipsis-start");
                        pages.push(currentPage - 1);
                        pages.push(currentPage);
                        pages.push(currentPage + 1);
                        pages.push("ellipsis-end");
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (typeof page === "string") {
                        return (
                          <li
                            key={`ellipsis-${index}`}
                            className="page-item disabled"
                          >
                            <span
                              className="page-link"
                              style={{
                                padding: "8px 16px",
                                minWidth: "45px",
                                textAlign: "center",
                                color: "var(--blue-color)",
                              }}
                            >
                              ...
                            </span>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={page}
                          className={`page-item ${
                            currentPage === page ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                            style={{
                              backgroundColor:
                                currentPage === page ? "var(--blue-color)" : "",
                              borderColor:
                                currentPage === page ? "var(--blue-color)" : "",
                              color:
                                currentPage === page
                                  ? "#fff"
                                  : "var(--blue-color)",
                              padding: "8px 16px",
                              minWidth: "45px",
                              textAlign: "center",
                            }}
                          >
                            {page}
                          </button>
                        </li>
                      );
                    });
                  })()}

                  <li
                    className={`page-item ${
                      currentPage === totalPages || totalRows === 0
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      style={{
                        padding: "8px 16px",
                        minWidth: "45px",
                        textAlign: "center",
                        color: "var(--blue-color)",
                      }}
                    >
                      Next &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <ConfirmationModal
            show={showConfirmationModal}
            handleCloseModal={handleCloseConfirmationModal}
            handleConfirmDelete={handleConfirmDelete}
            confirmTitle="card"
          />
        </div>
      )}
    </>
  );
};

export default AllCards;
